/**
 * Publicidad (1.0 AdminAds): inventario propio (home_top / home_feed / home_bottom /
 * product_sidebar) con impresiones, clics y CTR, y la configuración del ad server
 * (`settings/adServer`: proveedor activo, frecuencia en el feed, ids públicos de Google/Meta).
 * El servidor valida (admin-saveAdBanner) y audita; la imagen se sube a `uploads/{adminUid}/ads/`.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import {
  AD_PLACEMENTS,
  AD_PLACEMENT_SPECS,
  AD_PROVIDERS,
  AD_SERVER_DEFAULTS,
  type AdPlacement,
  type AdProvider,
  type AdServerSettings,
} from '@nuvvu/shared';
import { api } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { app, auth, db } from '../firebase.js';
import { btn, card, field, muted, Notice, str, Table, td, when } from '../lib/ui.js';

type Doc = Record<string, unknown>;

const PLACEMENT_LABEL: Record<AdPlacement, string> = {
  home_top: 'Portada — arriba',
  home_feed: 'Portada — feed (entre productos)',
  home_bottom: 'Portada — abajo',
  product_sidebar: 'Ficha de producto',
};
const PROVIDER_LABEL: Record<AdProvider, string> = {
  manual: 'Manual (solo campañas propias)',
  google: 'Google AdSense',
  meta: 'Meta Audience Network (solo apps)',
  hybrid: 'Híbrido (alterna Google / manual)',
  seguratech: 'SeguraTech Ads (si no sirve: campañas propias y luego Google)',
};
/** Panel de SeguraTech Ads (campañas, creatividades, informes). */
const SEGURATECH_ADMIN_URL = 'https://seguratech.es/admin/publicidad';

interface Form {
  id: string;
  name: string;
  type: 'banner_image' | 'html' | 'video';
  image_url: string;
  video_url: string;
  html_content: string;
  click_url: string;
  target_countries: string;
  placement: AdPlacement;
  is_active: boolean;
  starts_at: string;
  ends_at: string;
  reason: string;
}
const localInput = (d: Date | null): string => {
  if (!d) return '';
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const toDate = (v: unknown): Date | null =>
  v && typeof v === 'object' && 'toDate' in v ? (v as { toDate: () => Date }).toDate() : null;
const blank = (): Form => ({
  id: '',
  name: '',
  type: 'banner_image',
  image_url: '',
  video_url: '',
  html_content: '',
  click_url: '',
  target_countries: '',
  placement: 'home_feed',
  is_active: true,
  starts_at: '',
  ends_at: '',
  reason: '',
});
const fromDoc = (a: Doc): Form => ({
  id: str(a.id),
  name: str(a.name),
  type: a.type === 'html' ? 'html' : a.type === 'video' ? 'video' : 'banner_image',
  image_url: str(a.image_url),
  video_url: str(a.video_url),
  html_content: str(a.html_content),
  click_url: str(a.click_url),
  target_countries: ((a.target_countries as string[] | undefined) ?? []).join(', '),
  placement: (AD_PLACEMENTS as readonly string[]).includes(str(a.placement))
    ? (a.placement as AdPlacement)
    : 'home_feed',
  is_active: a.is_active === true,
  starts_at: localInput(toDate(a.starts_at)),
  ends_at: localInput(toDate(a.ends_at)),
  reason: '',
});
const list = (s: string): string[] =>
  s
    .split(/[,\s]+/)
    .map((x) => x.trim().toUpperCase())
    .filter(Boolean);
const ctr = (a: Doc): string => {
  const imp = Number(a.impressions ?? 0);
  const cl = Number(a.clicks ?? 0);
  return imp > 0 ? `${((cl / imp) * 100).toFixed(1)} %` : '—';
};

type Server = Omit<AdServerSettings, 'updated_at' | 'updated_by'>;

export function AdsPage(): ReactElement {
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [server, setServer] = useState<Server>({ ...AD_SERVER_DEFAULTS });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = (): void => {
    getDocs(query(collection(db, 'adBanners'), orderBy('created_at', 'desc')))
      .then((s) => {
        setRows(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch(() => {
        setRows([]);
      });
    getDoc(doc(db, 'settings/adServer'))
      .then((s) => {
        const d = s.data();
        if (d)
          setServer({
            provider: (AD_PROVIDERS as readonly string[]).includes(str(d.provider))
              ? (d.provider as AdProvider)
              : 'manual',
            google_ad_client_id: str(d.google_ad_client_id),
            google_ad_unit_id: str(d.google_ad_unit_id),
            meta_placement_id: str(d.meta_placement_id),
            feed_frequency: Number(d.feed_frequency ?? 8) || 8,
          });
      })
      .catch(() => undefined);
  };
  useEffect(load, []);

  const set = (k: keyof Form, v: string | boolean): void => {
    setForm((f) => (f ? { ...f, [k]: v } : f));
  };
  const saveServer = (): void => {
    setBusy(true);
    setError(null);
    call(api.admin.updateSettings, {
      doc: 'adServer',
      data: { ...server, feed_frequency: Math.min(50, Math.max(1, server.feed_frequency)) },
    })
      .then(() => {
        setNotice('Configuración del ad server guardada.');
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const upload = (file: File, field: 'image_url' | 'video_url' = 'image_url'): void => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    if (field === 'video_url' && file.size >= 20 * 1024 * 1024) {
      setError('El vídeo pesa más de 20 MB. Exporta un MP4 corto (4–10 s, 1280 px de ancho).');
      return;
    }
    setUploading(true);
    const safe = `${Date.now()}-${file.name.replace(/[^\w.-]+/g, '_')}`;
    const path = field === 'video_url' ? `ad_videos/${uid}/${safe}` : `uploads/${uid}/ads/${safe}`;
    uploadBytes(ref(getStorage(app), path), file, { contentType: file.type })
      .then((snap) => getDownloadURL(snap.ref))
      .then((url) => {
        set(field, url);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setUploading(false);
      });
  };
  const save = (): void => {
    if (!form) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    const data = {
      name: form.name.trim(),
      type: form.type,
      image_url: form.image_url.trim(),
      video_url: form.type === 'video' ? form.video_url.trim() : '',
      html_content: form.html_content,
      click_url: form.click_url.trim(),
      target_countries: list(form.target_countries),
      placement: form.placement,
      is_active: form.is_active,
      starts_at: form.starts_at ? new Date(form.starts_at).getTime() : null,
      ends_at: form.ends_at ? new Date(form.ends_at).getTime() : null,
    };
    call(api.admin.saveAdBanner, {
      ...(form.id ? { id: form.id } : {}),
      data,
      ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
    })
      .then((r) => {
        setNotice(`Anuncio guardado (${r.id}).`);
        setForm(null);
        load();
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const toggle = (a: Doc): void => {
    call(api.admin.saveAdBanner, {
      id: str(a.id),
      data: { ...a, is_active: a.is_active !== true },
      reason: a.is_active === true ? 'pausar' : 'reactivar',
    })
      .then(load)
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  const remove = (a: Doc): void => {
    if (!window.confirm(`¿Borrar el anuncio «${str(a.name)}»?`)) return;
    call(api.admin.deleteAdBanner, { id: str(a.id) })
      .then(() => {
        setNotice('Anuncio borrado.');
        load();
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };

  const totals = (rows ?? []).reduce<{ imp: number; cl: number }>(
    (acc, a) => ({
      imp: acc.imp + Number(a.impressions ?? 0),
      cl: acc.cl + Number(a.clicks ?? 0),
    }),
    { imp: 0, cl: 0 },
  );
  const spec = form ? AD_PLACEMENT_SPECS[form.placement] : null;

  return (
    <>
      <h1 style={{ margin: 0 }}>Publicidad</h1>
      <p style={muted}>
        Inventario vendido a anunciantes: banner arriba y abajo de la portada, tarjeta entre los
        productos del feed y tarjeta en la ficha de producto. Países vacíos = todos; fechas vacías =
        siempre. Todo lo pagado se muestra con la etiqueta «Patrocinado» (33 idiomas).
      </p>
      <Notice text={notice} />
      <Notice text={error} error />
      {server.provider === 'seguratech' && (
        <section
          style={{ ...card, display: 'grid', gap: 6, maxWidth: 640, borderColor: '#2563eb' }}
        >
          <strong>Las campañas se gestionan en SeguraTech Ads</strong>
          <span style={{ fontSize: 13 }}>
            Nuvvu solo pinta los anuncios que sirve SeguraTech (portada arriba, slider de portada,
            feed y ficha de producto) y le envía impresiones y clics. Altas, creatividades, fechas,
            segmentación e informes para el cliente:{' '}
            <a href={SEGURATECH_ADMIN_URL} target="_blank" rel="noreferrer">
              SeguraTech › Admin › Publicidad
            </a>
            .
          </span>
          <span style={{ ...muted, fontSize: 12 }}>
            Las campañas de esta página siguen activas como reserva: se enseñan cuando SeguraTech no
            tiene anuncio para ese hueco o no responde a tiempo (y después AdSense, si está
            configurado). «Portada — abajo» solo usa estas campañas.
          </span>
        </section>
      )}

      <section style={{ ...card, display: 'grid', gap: 10, maxWidth: 640 }}>
        <strong>Ad server (settings/adServer)</strong>
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Proveedor activo
          <select
            style={field}
            value={server.provider}
            onChange={(e) => {
              setServer((s) => ({ ...s, provider: e.target.value as AdProvider }));
            }}
          >
            {AD_PROVIDERS.map((p) => (
              <option key={p} value={p}>
                {PROVIDER_LABEL[p]}
              </option>
            ))}
          </select>
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Frecuencia en el feed (cada N productos)
            <input
              style={field}
              type="number"
              min={1}
              max={50}
              value={server.feed_frequency}
              onChange={(e) => {
                setServer((s) => ({ ...s, feed_frequency: Number(e.target.value) || 8 }));
              }}
            />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Google Ad Client (ca-pub-…)
            <input
              style={field}
              value={server.google_ad_client_id}
              placeholder="ca-pub-0000000000000000"
              onChange={(e) => {
                setServer((s) => ({ ...s, google_ad_client_id: e.target.value }));
              }}
            />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Google Ad Unit / Slot (numérico)
            <input
              style={field}
              value={server.google_ad_unit_id}
              placeholder="1234567890"
              onChange={(e) => {
                setServer((s) => ({ ...s, google_ad_unit_id: e.target.value }));
              }}
            />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Meta Placement ID (apps)
            <input
              style={field}
              value={server.meta_placement_id}
              onChange={(e) => {
                setServer((s) => ({ ...s, meta_placement_id: e.target.value }));
              }}
            />
          </label>
        </div>
        <button
          type="button"
          style={{ ...btn(true), justifySelf: 'start' }}
          disabled={busy}
          onClick={saveServer}
        >
          Guardar configuración
        </button>
      </section>

      <p style={muted}>
        {rows?.length ?? 0} anuncios · {totals.imp.toLocaleString('es')} impresiones ·{' '}
        {totals.cl.toLocaleString('es')} clics
      </p>
      {!rows && <p style={muted}>Cargando…</p>}
      {rows && (
        <Table
          head={[
            '',
            'Nombre',
            'Hueco',
            'Activo',
            'Países',
            'Impr.',
            'Clics',
            'CTR',
            'Desde',
            'Hasta',
            '',
          ]}
        >
          {rows.map((a) => (
            <tr key={str(a.id)} style={{ opacity: a.is_active === true ? 1 : 0.6 }}>
              <td style={td}>
                {a.image_url ? (
                  <img
                    src={str(a.image_url)}
                    alt=""
                    width={64}
                    height={40}
                    style={{ objectFit: 'cover', borderRadius: 6 }}
                  />
                ) : (
                  <span style={muted}>HTML</span>
                )}
              </td>
              <td style={td}>
                {str(a.name)}
                {a.auto === 'pro_absolute' && (
                  <div style={{ ...muted, fontSize: 11 }}>
                    Automático PRO Absoluto (lo actualiza el job diario)
                  </div>
                )}
              </td>
              <td style={td}>{PLACEMENT_LABEL[fromDoc(a).placement]}</td>
              <td style={td}>{a.is_active === true ? 'sí' : 'no'}</td>
              <td style={td}>
                {((a.target_countries as string[] | undefined) ?? []).join(', ') || '*'}
              </td>
              <td style={td}>{Number(a.impressions ?? 0).toLocaleString('es')}</td>
              <td style={td}>{Number(a.clicks ?? 0).toLocaleString('es')}</td>
              <td style={td}>{ctr(a)}</td>
              <td style={td}>{a.starts_at ? when(a.starts_at) : '—'}</td>
              <td style={td}>{a.ends_at ? when(a.ends_at) : '—'}</td>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>
                <button
                  type="button"
                  style={btn(false)}
                  onClick={() => {
                    setForm(fromDoc(a));
                  }}
                >
                  Editar
                </button>{' '}
                <button
                  type="button"
                  style={btn(false)}
                  onClick={() => {
                    toggle(a);
                  }}
                >
                  {a.is_active === true ? 'Pausar' : 'Activar'}
                </button>{' '}
                <button
                  type="button"
                  style={btn(false, true)}
                  onClick={() => {
                    remove(a);
                  }}
                >
                  Borrar
                </button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td style={td} colSpan={11}>
                Sin anuncios.
              </td>
            </tr>
          )}
        </Table>
      )}
      {!form && (
        <button
          type="button"
          style={{ ...btn(true), justifySelf: 'start' }}
          onClick={() => {
            setForm(blank());
          }}
        >
          Nuevo anuncio
        </button>
      )}
      {form && spec && (
        <form
          style={{ ...card, display: 'grid', gap: 10, maxWidth: 640 }}
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <strong>{form.id ? `Editar ${form.id}` : 'Nuevo anuncio'}</strong>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Nombre de la campaña
            <input
              style={field}
              value={form.name}
              required
              onChange={(e) => {
                set('name', e.target.value);
              }}
            />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Tipo
              <select
                style={field}
                value={form.type}
                onChange={(e) => {
                  set('type', e.target.value);
                }}
              >
                <option value="banner_image">Imagen</option>
                <option value="html">HTML (sin scripts)</option>
                <option value="video">Vídeo (MP4, 4–10 s, en bucle y sin sonido)</option>
              </select>
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Hueco
              <select
                style={field}
                value={form.placement}
                onChange={(e) => {
                  set('placement', e.target.value);
                }}
              >
                {AD_PLACEMENTS.map((p) => (
                  <option key={p} value={p}>
                    {PLACEMENT_LABEL[p]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p style={muted}>
            {spec.hint} Medida recomendada: {spec.size} (proporción {spec.ratio}).
          </p>
          {form.type === 'banner_image' ? (
            <div style={{ display: 'grid', gap: 6 }}>
              <div
                style={{
                  aspectRatio: spec.ratio === '4:1' ? '4 / 1' : '1 / 1',
                  maxWidth: spec.ratio === '4:1' ? 560 : 240,
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: '#f3f3f3',
                }}
              >
                {form.image_url && (
                  <img
                    src={form.image_url}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) upload(f);
                }}
              />
              {uploading && <span style={muted}>Subiendo…</span>}
              <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
                URL de la imagen
                <input
                  style={field}
                  value={form.image_url}
                  placeholder="https://…"
                  onChange={(e) => {
                    set('image_url', e.target.value);
                  }}
                />
              </label>
            </div>
          ) : form.type === 'video' ? (
            <div style={{ display: 'grid', gap: 6 }}>
              <div
                style={{
                  aspectRatio: spec.ratio === '4:1' ? '4 / 1' : '1 / 1',
                  maxWidth: spec.ratio === '4:1' ? 560 : 240,
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: '#f3f3f3',
                }}
              >
                {form.video_url ? (
                  <video
                    key={form.video_url}
                    src={form.video_url}
                    poster={form.image_url || undefined}
                    autoPlay
                    muted
                    loop
                    playsInline
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  form.image_url && (
                    <img
                      src={form.image_url}
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  )
                )}
              </div>
              <p style={muted}>
                Sube un MP4 (H.264) o WebM de 4–10 segundos, máximo 20 MB, con la misma proporción
                del hueco. Se reproduce en bucle y sin sonido. La portada es obligatoria: se ve
                mientras carga, si el usuario tiene «reducir movimiento» y en las apps hasta su
                próxima versión.
              </p>
              <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
                Vídeo
                <input
                  type="file"
                  accept="video/mp4,video/webm"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) upload(f, 'video_url');
                  }}
                />
              </label>
              <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
                Portada (imagen)
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) upload(f);
                  }}
                />
              </label>
              {uploading && <span style={muted}>Subiendo…</span>}
              <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
                URL del vídeo
                <input
                  style={field}
                  value={form.video_url}
                  placeholder="https://…"
                  onChange={(e) => {
                    set('video_url', e.target.value);
                  }}
                />
              </label>
              <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
                URL de la portada
                <input
                  style={field}
                  value={form.image_url}
                  placeholder="https://…"
                  onChange={(e) => {
                    set('image_url', e.target.value);
                  }}
                />
              </label>
            </div>
          ) : (
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              HTML (se pinta en un iframe aislado: sin scripts ni formularios)
              <textarea
                style={{ ...field, fontFamily: 'monospace', minHeight: 120 }}
                value={form.html_content}
                onChange={(e) => {
                  set('html_content', e.target.value);
                }}
              />
            </label>
          )}
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            URL de destino al hacer clic
            <input
              style={field}
              value={form.click_url}
              placeholder="https://…"
              onChange={(e) => {
                set('click_url', e.target.value);
              }}
            />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Países (ISO-2, separados por comas; vacío = todos)
            <input
              style={field}
              value={form.target_countries}
              placeholder="HU, RO, SK"
              onChange={(e) => {
                set('target_countries', e.target.value);
              }}
            />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Desde (opcional)
              <input
                style={field}
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => {
                  set('starts_at', e.target.value);
                }}
              />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Hasta (opcional)
              <input
                style={field}
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => {
                  set('ends_at', e.target.value);
                }}
              />
            </label>
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => {
                set('is_active', e.target.checked);
              }}
            />
            Activo
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Motivo (auditoría)
            <input
              style={field}
              value={form.reason}
              onChange={(e) => {
                set('reason', e.target.value);
              }}
            />
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" style={btn(true)} disabled={busy || uploading}>
              Guardar
            </button>
            <button
              type="button"
              style={btn(false)}
              onClick={() => {
                setForm(null);
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}
    </>
  );
}
